import { useEffect, useRef } from 'react';

/**
 * CLOSE A LITTLE MENU WHEN THE NEXT CLICK LANDS ANYWHERE ELSE.
 *
 * ⚠️ THIS EXISTS BECAUSE THE OBVIOUS VERSION DOES NOT WORK, AND SHIPPED BROKEN TWICE.
 *
 * The obvious version is four lines in the caller:
 *
 *     useEffect(() => {
 *       if (!open) return;
 *       document.addEventListener('click', close);   // ← closes the menu it just opened
 *       return () => document.removeEventListener('click', close);
 *     }, [open]);
 *
 * Both copies of it carried the same comment — *"registered only while open, and after the
 * click that opened it, so it catches only later clicks"* — and that second clause is FALSE.
 * React 18 flushes the passive effect for a discrete event's update synchronously, while the
 * click that caused it is STILL BUBBLING. So `document` gets its listener at the button, and
 * the very same click then reaches `document` and closes the menu. Measured, not reasoned
 * (0.1.403, a probe that wrapped `document.addEventListener` and logged the order):
 *
 *     ["ADD document click listener", "CLICK reached document (bubble)"]
 *
 * The maintenance row's ⋯ menu was therefore IMPOSSIBLE to open — reported from the phone as
 * "I cannot click on the options menu in each row" — and the pickers' Manage… menu had the
 * identical defect, unreported only because it is reached less often.
 *
 * THE FIX IS TO IGNORE THAT ONE EVENT BY IDENTITY. The effect captures whatever click is
 * mid-flight at the moment it runs and skips exactly that one; every later click closes.
 *
 * Two alternatives were tried on paper and rejected, both for reasons worth keeping:
 *  · `queueMicrotask` to register after the dispatch — WRONG. A microtask checkpoint runs each
 *    time the JS stack empties, and event dispatch calls each listener as its own script entry,
 *    so the checkpoint fires between React's root handler and `document`. It would register in
 *    time to catch the very click it is meant to miss.
 *  · `setTimeout(…, 0)` — works (a macrotask really does land after the whole dispatch), but it
 *    couples correctness to scheduling. Identity does not: whichever way React decides to flush
 *    effects in future, "not the event that opened it" stays true.
 *
 * `window.event` is the in-flight event during dispatch and `undefined` otherwise. It is old,
 * and it is also standardised (WHATWG HTML) precisely so this stays available. When the menu is
 * opened by something other than a click — a keyboard Enter, a programmatic open — there is
 * nothing in flight to skip and every click closes, which is also the correct behaviour.
 *
 * Hoisted rather than repaired twice (DRY: this is one FACT about the platform, so it belongs
 * in one place). A third caller gets it right by construction instead of by remembering.
 */
export function useDismissOnOutsideClick(open: boolean, close: () => void): void {
  // The latest callback, without making it a dependency -- see the effect's closing comment.
  const latest = useRef(close);
  latest.current = close;

  useEffect(() => {
    if (!open) return;
    // The click still propagating right now, if any — see the header.
    const opening: Event | undefined = window.event ?? undefined;
    const onClick = (e: MouseEvent): void => {
      if (e === opening) return; // the click that opened it is not a click "elsewhere"
      latest.current();
    };
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
    // `close` is deliberately NOT a dependency: it is a fresh closure every render, and
    // re-registering the listener on each one would re-capture `window.event` as `undefined`
    // and lose the skip. A ref keeps the latest callback reachable without that.
  }, [open]);
}
